import { useState, useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { restClient } from '@/api/client'
import { useDebounce } from '@/shared/hooks'
import { cn, DUMMY_AVATAR, onAvatarError } from '@/shared/utils'
import {
  Search, Download, Calendar, X, Eye, Mail, Phone,
  ChevronLeft, ChevronRight, CreditCard, Receipt,
  FileText, Clock, CheckCircle, XCircle,
} from 'lucide-react'

// ── Helpers ───────────────────────────────────────────────────────────────────
function fmtDT(v?: string | null) {
  if (!v) return '—'
  try {
    return new Date(v).toLocaleString('en-GB', {
      day:'2-digit', month:'2-digit', year:'numeric',
      hour:'2-digit', minute:'2-digit', hour12: false,
    }).replace(',', ',')
  } catch { return v }
}

// Matches the real status enum: PENDING=0, CONFIRMED=1, CANCELLED=2, COMPLETED=3, IN PROGRESS=4
function statusCls(s?: string) {
  const u = (s ?? '').toUpperCase()
  if (u === 'PENDING')      return 'bg-brand-500 text-white'
  if (u === 'CONFIRMED')    return 'bg-blue-500 text-white'
  if (u === 'CANCELLED')    return 'bg-red-500 text-white'
  if (u === 'COMPLETED')    return 'bg-green-500 text-white'
  if (u === 'IN PROGRESS')  return 'bg-purple-500 text-white'
  return 'bg-gray-200 text-gray-700'
}
// Matches the real payment_status enum: PENDING=0, PAID=1, REFUNDED=2, PARTIALLY PAID=3, FAILED=4
function payStatusCls(s?: string) {
  const u = (s ?? '').toUpperCase()
  if (u === 'PENDING')         return 'bg-brand-100 text-brand-700 border border-brand-300'
  if (u === 'PAID')            return 'bg-green-100 text-green-700 border border-green-300'
  if (u === 'REFUNDED')        return 'bg-purple-100 text-purple-700 border border-purple-300'
  if (u === 'PARTIALLY PAID')  return 'bg-amber-100 text-amber-700 border border-amber-300'
  if (u === 'FAILED')          return 'bg-red-100 text-red-600 border border-red-300'
  return 'bg-gray-100 text-gray-600'
}
function cardBg(s?: string) {
  const u = (s ?? '').toUpperCase()
  if (u === 'PENDING')      return 'bg-brand-50/40'
  if (u === 'CONFIRMED')    return 'bg-blue-50/50'
  if (u === 'CANCELLED')    return 'bg-red-50/40'
  if (u === 'COMPLETED')    return 'bg-green-50/50'
  if (u === 'IN PROGRESS')  return 'bg-purple-50/40'
  return 'bg-white'
}

// ── Detail Modal ──────────────────────────────────────────────────────────────
function DetailModal({ open, onClose, registration }: {
  open: boolean; onClose: () => void; registration: any | null
}) {
  const { t } = useTranslation()
  const registrationId = registration?.id ?? null
  const [status,        setStatus]        = useState('')
  const [paymentStatus, setPaymentStatus] = useState('')
  const [note,          setNote]          = useState('')
  const [loading,       setLoading]       = useState(false)
  const qc = useQueryClient()

  const { data, isLoading } = useQuery({
    queryKey: ['service-registration-detail', registrationId],
    queryFn: () => restClient.get(`admin/dynamic-service/registrations/get-by-id/${registrationId}`).then(r=>r.data),
    enabled: open && !!registrationId,
  })

  // Merge the list row (always present) with the fetched detail (enhancement).
  const detail = { ...(registration ?? {}), ...((data as any)?.data ?? {}) }
  const user   = detail.user ?? {}
  const payment = detail.payment ?? detail.transaction ?? {}
  // Build Emirates ID documents from the flat *_url fields, falling back to any documents array.
  const documents: any[] = [
    detail.emirates_id_front_url && { side: t('servicesPage.detail.frontSide'), file_url: detail.emirates_id_front_url },
    detail.emirates_id_back_url  && { side: t('servicesPage.detail.backSide'),  file_url: detail.emirates_id_back_url },
  ].filter(Boolean)
  const docList = documents.length ? documents : (detail.documents ?? detail.user_documents ?? [])

  const handleSubmit = async () => {
    setLoading(true)
    try {
      const payload: Record<string, unknown> = { registration_id: registrationId }
      if (status)        payload.status = Number(status)
      if (paymentStatus) payload.payment_status = Number(paymentStatus)
      if (note)          payload.admin_note = note
      await restClient.post('admin/dynamic-service/registrations/update-status', payload)
      qc.invalidateQueries({ queryKey: ['services'] })
      onClose()
    } catch {}
    finally { setLoading(false) }
  }

  if (!open) return null
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/40" onClick={onClose}/>
      <div className="relative bg-gray-50 rounded-2xl shadow-2xl w-full max-w-5xl max-h-[90vh] overflow-y-auto">
        <div className="p-6 grid grid-cols-1 lg:grid-cols-2 gap-5">

          {/* ── Left: User & Service Info ── */}
          <div className="space-y-4">
            <div className="bg-white rounded-2xl border border-gray-200 shadow-sm overflow-hidden">
              <div className="px-5 py-3 bg-brand-500 flex items-center gap-2">
                <div className="w-5 h-5 rounded-full bg-white/30 flex items-center justify-center">
                  <span className="text-[12px] text-white font-bold">U</span>
                </div>
                <span className="text-sm font-bold text-white">{t('servicesPage.detail.userServiceInfo')}</span>
              </div>
              {isLoading ? (
                <div className="p-5 space-y-2 animate-pulse">
                  {[1,2,3,4].map(i=><div key={i} className="h-4 bg-gray-100 rounded w-3/4"/>)}
                </div>
              ) : (
                <div className="p-5 grid grid-cols-2 gap-x-6 gap-y-3">
                  {[
                    { icon:<span className="text-gray-400">👤</span>, label:t('servicesPage.detail.userName'),    value: detail.registrant_name ?? detail.user_name ?? user.name },
                    { icon:<Phone size={12} className="text-gray-400"/>, label:t('servicesPage.detail.phoneNumber'), value: (detail.registrant_phone ?? detail.user_phone ?? user.phone) ? `+971 ${detail.registrant_phone ?? detail.user_phone ?? user.phone}` : null },
                    { icon:<Mail size={12} className="text-gray-400"/>, label:t('servicesPage.detail.email'),       value: detail.registrant_email || user.email || null },
                    { icon:<FileText size={12} className="text-gray-400"/>, label:t('servicesPage.detail.emiratesId'), value: detail.emirates_id_number },
                    { icon:<Calendar size={12} className="text-gray-400"/>, label:t('servicesPage.detail.appliedDate'), value: fmtDT(detail.created_at ?? detail.applied_at) },
                    { icon:<FileText size={12} className="text-gray-400"/>, label:t('servicesPage.detail.service'), value: detail.service_name },
                  ].map(({ icon, label, value }) => (
                    <div key={label} className="flex flex-col gap-0.5">
                      <div className="text-[11px] text-gray-400 uppercase font-semibold flex items-center gap-1">{icon} {label}</div>
                      <div className="text-sm font-semibold text-gray-900">{value ?? '—'}</div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Emirates ID / documents */}
            <div className="bg-white rounded-2xl border border-gray-200 shadow-sm overflow-hidden">
              <div className="px-5 py-3 bg-brand-600 flex items-center gap-2">
                <span className="text-sm font-bold text-white">{t('servicesPage.detail.emiratesId')}</span>
              </div>
              {detail.emirates_id_number && (
                <div className="px-4 pt-3 text-xs text-gray-500">{t('servicesPage.detail.idNumber')} <span className="font-semibold text-gray-800">{detail.emirates_id_number}</span></div>
              )}
              <div className="p-4 flex flex-wrap gap-3">
                {docList.length === 0 ? (
                  <p className="text-xs text-gray-400 italic">{t('servicesPage.detail.noDocuments')}</p>
                ) : docList.map((doc: any, i: number) => {
                  const url = doc.file_url ?? doc.virtual_path ?? doc.doc_file
                  return (
                  <a key={i} href={url} target="_blank" rel="noreferrer" className="relative rounded-xl overflow-hidden border border-gray-200 shadow-sm group">
                    <div className="absolute top-0 left-0 right-0 bg-brand-500 text-white text-[11px] font-bold px-2 py-0.5 flex items-center justify-between z-10">
                      <span>{doc.side ?? (i===0?t('servicesPage.detail.frontSide'):t('servicesPage.detail.backSide'))}</span>
                      <Eye size={10}/>
                    </div>
                    {url ? (
                      <img src={url} alt={t('servicesPage.detail.idAlt')} className="w-36 h-24 object-cover mt-5 group-hover:opacity-90"/>
                    ) : (
                      <div className="w-36 h-24 mt-5 bg-gray-100 flex items-center justify-center">
                        <FileText size={20} className="text-gray-300"/>
                      </div>
                    )}
                  </a>
                )})}
              </div>
            </div>
          </div>

          {/* ── Right: Payment + Update Status ── */}
          <div className="space-y-4">
            <div className="bg-white rounded-2xl border border-gray-200 shadow-sm overflow-hidden">
              <div className="px-5 py-3 bg-brand-700 flex items-center gap-2">
                <CreditCard size={14} className="text-white"/>
                <span className="text-sm font-bold text-white">{t('servicesPage.detail.paymentDetails')}</span>
              </div>
              {isLoading ? (
                <div className="p-5 space-y-2 animate-pulse">
                  {[1,2,3].map(i=><div key={i} className="h-4 bg-gray-100 rounded w-2/3"/>)}
                </div>
              ) : (
                <div className="p-5 grid grid-cols-2 gap-x-6 gap-y-3">
                  <div>
                    <div className="text-[11px] text-gray-400 uppercase font-semibold">{t('servicesPage.detail.amountPaid')}</div>
                    <div className="text-sm font-black text-gray-900">AED {detail.amount_paid ?? detail.service_fees ?? detail.amount ?? payment.amount ?? '—'}</div>
                  </div>
                  <div>
                    <div className="text-[11px] text-gray-400 uppercase font-semibold">{t('servicesPage.detail.serviceFees')}</div>
                    <div className="text-sm font-semibold text-gray-900">AED {detail.service_fees ?? '—'}</div>
                  </div>
                  <div>
                    <div className="text-[11px] text-gray-400 uppercase font-semibold">{t('servicesPage.detail.paymentStatus')}</div>
                    <span className={cn('px-2.5 py-0.5 rounded-full text-[12px] font-bold', payStatusCls(detail.payment_status_label ?? payment.status))}>
                      {detail.payment_status_label ?? payment.status ?? '—'}
                    </span>
                  </div>
                  <div>
                    <div className="text-[11px] text-gray-400 uppercase font-semibold">{t('servicesPage.detail.transactionId')}</div>
                    <div className="text-sm font-semibold text-gray-900 flex items-center gap-1">
                      <Receipt size={12} className="text-gray-400"/>
                      {detail.transaction_id ? `#${detail.transaction_id}` : payment.id ? `#${payment.id}` : '—'}
                    </div>
                  </div>
                  <div className="col-span-2">
                    <div className="text-[11px] text-gray-400 uppercase font-semibold">{t('servicesPage.detail.appliedAt')}</div>
                    <div className="text-sm font-semibold text-gray-900 flex items-center gap-1">
                      <Calendar size={12} className="text-gray-400"/>{fmtDT(detail.applied_at ?? detail.created_at)}
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Update Status */}
            <div className="bg-white rounded-2xl border border-gray-200 shadow-sm overflow-hidden">
              <div className="px-5 py-3 bg-gray-100 border-b border-gray-200">
                <span className="text-sm font-bold text-gray-800">{t('servicesPage.detail.updateStatus')}</span>
              </div>
              <div className="p-5 space-y-3">
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="text-xs font-semibold text-gray-600 block mb-1">{t('servicesPage.detail.selectStatus')}</label>
                    <select value={status} onChange={e=>setStatus(e.target.value)}
                      className="w-full text-sm border border-gray-200 rounded-xl px-3 py-2 focus:outline-none focus:ring-2 focus:ring-brand-400 bg-white">
                      <option value="">{t('servicesPage.detail.status')}</option>
                      <option value="0">{t('servicesPage.detail.pending')}</option>
                      <option value="1">{t('servicesPage.detail.confirmed')}</option>
                      <option value="2">{t('servicesPage.detail.cancelled')}</option>
                      <option value="3">{t('servicesPage.detail.completed')}</option>
                      <option value="4">{t('servicesPage.detail.inProgress')}</option>
                    </select>
                  </div>
                  <div>
                    <label className="text-xs font-semibold text-gray-600 block mb-1">{t('servicesPage.detail.selectPaymentStatus')}</label>
                    <select value={paymentStatus} onChange={e=>setPaymentStatus(e.target.value)}
                      className="w-full text-sm border border-gray-200 rounded-xl px-3 py-2 focus:outline-none focus:ring-2 focus:ring-brand-400 bg-white">
                      <option value="">{t('servicesPage.detail.paymentStatus')}</option>
                      <option value="0">{t('servicesPage.detail.payPending')}</option>
                      <option value="1">{t('servicesPage.detail.payPaid')}</option>
                      <option value="2">{t('servicesPage.detail.payRefunded')}</option>
                      <option value="3">{t('servicesPage.detail.payPartiallyPaid')}</option>
                      <option value="4">{t('servicesPage.detail.payFailed')}</option>
                    </select>
                  </div>
                </div>
                <div>
                  <label className="text-xs font-semibold text-gray-600 block mb-1">{t('servicesPage.detail.message')}</label>
                  <textarea value={note} onChange={e=>setNote(e.target.value)} rows={4}
                    placeholder={t('servicesPage.detail.textAreaPlaceholder')}
                    className="w-full text-sm border border-gray-200 rounded-xl px-3 py-2 focus:outline-none focus:ring-2 focus:ring-brand-400 resize-none placeholder-gray-300"/>
                </div>
                <div className="flex justify-end">
                  <button onClick={handleSubmit} disabled={loading}
                    className="px-6 py-2 rounded-xl border border-brand-400 text-brand-600 text-sm font-semibold hover:bg-brand-50 transition-colors disabled:opacity-50">
                    {loading ? t('servicesPage.detail.submitting') : t('servicesPage.detail.submit')}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

// ── Service card ──────────────────────────────────────────────────────────────
function ServiceCard({ row, onView }: { row: any; onView: () => void }) {
  const { t } = useTranslation()
  const user   = row.user ?? {}
  const status = row.status_label ?? row.status ?? 'Pending'
  const pStatus = row.payment_status_label ?? row.payment_status ?? ''
  const bg = cardBg(status)

  // API is flat: registrant_name / registrant_email / registrant_phone / user_phone / amount_paid / service_fees
  const name  = row.registrant_name ?? row.user_name ?? user.name ?? '—'
  const email = (row.registrant_email || user.email || row.email) || '—'
  const rawPhone = row.registrant_phone ?? row.user_phone ?? user.phone ?? row.phone
  const phone = rawPhone ? `+971 ${rawPhone}` : '—'
  const reqId = `#SR${String(row.id ?? '0').padStart(4,'0')}`
  const dateTime = fmtDT(row.created_at ?? row.applied_at)
  const serviceName = row.service_name ?? '—'
  const amount = row.amount_paid ?? row.service_fees ?? row.amount ?? '—'
  const servicePrice = row.service_fees ?? '—'
  const appliedAt = fmtDT(row.applied_at ?? row.created_at)

  const photoUrl = user.photo_url ?? null

  return (
    <div className={cn('rounded-2xl border border-gray-200 shadow-sm hover:shadow-md transition-shadow overflow-hidden', bg)}>

      {/* Header */}
      <div className="flex items-start justify-between gap-3 p-4">
        <div className="flex items-center gap-3 min-w-0">
          <img src={photoUrl || DUMMY_AVATAR} alt={name} onError={onAvatarError}
            className="w-10 h-10 rounded-full object-cover border-2 border-white shadow-sm flex-shrink-0 bg-gray-100"/>
          <div className="min-w-0">
            <div className="text-sm font-bold text-gray-900 truncate">{name}</div>
            <div className="text-[12px] text-gray-400">{t('servicesPage.card.requestId')} {reqId}</div>
          </div>
        </div>
        <span className={cn('px-2.5 py-0.5 rounded-full text-xs font-bold flex-shrink-0', statusCls(status))}>
          {status}
        </span>
      </div>

      {/* Contact */}
      <div className="px-4 pb-2 space-y-0.5">
        <div className="flex items-center justify-between gap-2 text-xs text-gray-500">
          <span className="flex items-center gap-1 min-w-0"><Mail size={10} className="text-gray-400 flex-shrink-0"/><span className="truncate">{email}</span></span>
          <span className="flex items-center gap-1 flex-shrink-0"><Phone size={10} className="text-gray-400"/>{phone}</span>
        </div>
        <div className="flex items-center gap-1 text-xs text-gray-500">
          <Calendar size={10} className="text-gray-400"/>{dateTime}
        </div>
      </div>

      {/* Service Info */}
      <div className="mx-4 mb-2 rounded-xl border border-gray-200 overflow-hidden bg-white/60">
        <div className="px-3 py-1.5 text-xs font-bold text-gray-700 bg-gray-50/80">{t('servicesPage.card.serviceInfo')}</div>
        <div className="px-3 py-2 flex items-center gap-2">
          <FileText size={13} className="text-gray-400 flex-shrink-0"/>
          <span className="text-xs font-semibold text-gray-800">{serviceName}</span>
        </div>
      </div>

      {/* Payment Info */}
      <div className="mx-4 mb-3 rounded-xl border border-gray-200 overflow-hidden bg-white/60">
        <div className="px-3 py-1.5 text-xs font-bold text-gray-700 bg-gray-50/80">{t('servicesPage.card.paymentInfo')}</div>
        <div className="p-3 grid grid-cols-2 gap-x-4 gap-y-1.5">
          <div>
            <div className="text-[11px] text-gray-400 uppercase font-semibold">{t('servicesPage.detail.amountPaid')}</div>
            <div className="text-xs font-black text-gray-900 flex items-center gap-1">
              <span className="text-gray-400 text-[12px]">AED</span> {amount}
            </div>
          </div>
          <div>
            <div className="text-[11px] text-gray-400 uppercase font-semibold">{t('servicesPage.detail.serviceFees')}</div>
            <div className="text-xs font-semibold text-gray-800 flex items-center gap-1">
              <span className="text-gray-400 text-[12px]">AED</span> {servicePrice}
            </div>
          </div>
          <div>
            <div className="text-[11px] text-gray-400 uppercase font-semibold">{t('servicesPage.card.paymentStatus')}</div>
            <span className={cn('px-2 py-0.5 rounded-full text-[12px] font-bold', payStatusCls(pStatus))}>
              {pStatus || '—'}
            </span>
          </div>
          <div>
            <div className="text-[11px] text-gray-400 uppercase font-semibold">{t('servicesPage.card.appliedAt')}</div>
            <div className="text-xs font-semibold text-gray-800 flex items-center gap-1">
              <Calendar size={10} className="text-gray-400"/>{appliedAt}
            </div>
          </div>
        </div>
      </div>

      {/* View Details */}
      <div className="px-4 pb-4">
        <button onClick={onView}
          className="w-full py-2 rounded-xl border border-brand-300 text-brand-600 text-xs font-semibold flex items-center justify-center gap-1.5 hover:bg-brand-50 transition-colors">
          <Eye size={12}/> {t('servicesPage.card.viewDetails')}
        </button>
      </div>
    </div>
  )
}

// ── Skeleton ──────────────────────────────────────────────────────────────────
function SkeletonCard() {
  return (
    <div className="rounded-2xl border border-gray-200 bg-white animate-pulse overflow-hidden">
      <div className="p-4 flex items-center gap-3">
        <div className="w-10 h-10 rounded-full bg-gray-200 flex-shrink-0"/>
        <div className="flex-1 space-y-1.5"><div className="h-3 bg-gray-200 rounded w-2/3"/><div className="h-2 bg-gray-100 rounded w-1/3"/></div>
        <div className="h-5 w-16 rounded-full bg-gray-200"/>
      </div>
      <div className="px-4 pb-2 space-y-1"><div className="h-2 bg-gray-100 rounded w-3/4"/><div className="h-2 bg-gray-100 rounded w-1/2"/></div>
      <div className="mx-4 mb-2 h-10 bg-gray-100 rounded-xl"/>
      <div className="mx-4 mb-3 h-20 bg-gray-100 rounded-xl"/>
      <div className="px-4 pb-4"><div className="h-8 bg-gray-100 rounded-xl"/></div>
    </div>
  )
}

// ── Paginator ─────────────────────────────────────────────────────────────────
function Paginator({ page, lastPage, total, perPage, onPage, onPerPage }: {
  page:number; lastPage:number; total:number; perPage:number
  onPage:(p:number)=>void; onPerPage:(n:number)=>void
}) {
  const { t } = useTranslation()
  return (
    <div className="flex items-center justify-between gap-4 flex-wrap">
      <div className="flex items-center gap-2 text-xs text-gray-500">
        <span>{t('servicesPage.paginator.itemsPerPage')}</span>
        <select value={perPage} onChange={e=>onPerPage(Number(e.target.value))}
          className="border border-gray-200 rounded-lg px-2 py-1 text-xs focus:outline-none focus:ring-2 focus:ring-brand-400">
          {[10,15,20,25,50].map(n=><option key={n} value={n}>{n}</option>)}
        </select>
        <span>{t('servicesPage.paginator.itemsRange', { to: Math.min(page*perPage,total), total })}</span>
      </div>
      <div className="flex items-center gap-2">
        <button onClick={()=>onPage(Math.max(1,page-1))} disabled={page<=1}
          className="flex items-center gap-1 px-3 py-1.5 rounded-lg border border-gray-200 text-xs text-gray-500 hover:bg-gray-50 disabled:opacity-40 transition-colors">
          <ChevronLeft size={13} className="rtl:rotate-180"/> {t('servicesPage.paginator.previous')}
        </button>
        <div className="flex items-center gap-1">
          <input type="number" value={page} readOnly
            className="w-10 text-center text-xs border border-gray-200 rounded-lg py-1"/>
          <span className="text-xs text-gray-500">{t('servicesPage.paginator.of', { lastPage })}</span>
        </div>
        <button onClick={()=>onPage(Math.min(lastPage,page+1))} disabled={page>=lastPage}
          className="flex items-center gap-1 px-3 py-1.5 rounded-lg border border-gray-200 text-xs text-gray-500 hover:bg-gray-50 disabled:opacity-40 transition-colors">
          {t('servicesPage.paginator.next')} <ChevronRight size={13} className="rtl:rotate-180"/>
        </button>
      </div>
    </div>
  )
}

// ══════════════════════════════════════════════════════════════════════════════
// Main Page
// ══════════════════════════════════════════════════════════════════════════════
export function ServicesPage() {
  const { t } = useTranslation()
  const qc = useQueryClient()
  // Pre-filled when arriving from a student's "Services" tag on the user list.
  const [search,   setSearch]   = useState(() => new URLSearchParams(window.location.search).get('search') ?? '')
  const [page,     setPage]     = useState(1)
  const [perPage,  setPerPage]  = useState(20)
  const [payMethod, setPayMethod] = useState('')
  const [detailRow, setDetailRow] = useState<any|null>(null)

  const debouncedSearch = useDebounce(search)

  const { data, isLoading } = useQuery({
    queryKey: ['services','list',{ page, perPage, search: debouncedSearch, payMethod }],
    queryFn: () => {
      const params = new URLSearchParams({ page: String(page), limit: String(perPage) })
      if (debouncedSearch) params.append('search', debouncedSearch)
      if (payMethod)       params.append('payment_method', payMethod)
      return restClient.get(`admin/dynamic-service/registrations/get-list?${params}`).then(r=>r.data)
    },
    staleTime: 30_000,
  })

  const rows: any[] = (data as any)?.data ?? []
  const total    = (data as any)?.meta?.total ?? (data as any)?.total ?? rows.length
  const lastPage = Math.ceil(total / perPage) || 1

  const invalidate = useCallback(() => qc.invalidateQueries({ queryKey: ['services'] }), [qc])

  const handleExport = () => {
    const csv = [[t('servicesPage.csv.id'),t('servicesPage.csv.name'),t('servicesPage.csv.service'),t('servicesPage.csv.status'),t('servicesPage.csv.paymentStatus'),t('servicesPage.csv.date')],
      ...rows.map((r:any)=>[r.id, r.registrant_name??r.user?.name??'', r.service_name??'', r.status_label??'', r.payment_status_label??'', r.created_at??''])
    ].map(r=>r.join(',')).join('\n')
    const a=document.createElement('a'); a.href=URL.createObjectURL(new Blob([csv],{type:'text/csv'})); a.download='services.csv'; a.click()
  }

  return (
    <div className="space-y-5">

      {/* ── Header ── */}
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <div>
          <div className="flex items-center gap-1.5 text-xs text-gray-400 mb-1">
            <span>{t('servicesPage.breadcrumb.dashboard')}</span><span>/</span>
            <span className="text-brand-500 font-semibold">{t('servicesPage.breadcrumb.services')}</span>
          </div>
          <h1 className="text-xl font-bold text-gray-900">{t('servicesPage.title')}</h1>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <button onClick={handleExport}
            className="flex items-center gap-1.5 px-3 py-2 rounded-lg bg-green-500 hover:bg-green-600 text-white text-xs font-semibold transition-colors">
            <Download size={12}/> {t('servicesPage.download')}
          </button>
          <select value={payMethod} onChange={e=>{setPayMethod(e.target.value);setPage(1)}}
            className="text-xs border border-gray-200 rounded-lg px-3 py-2 text-gray-500 focus:outline-none min-w-32">
            <option value="">{t('servicesPage.filters.paymentMethod')}</option>
            <option value="Cash">{t('servicesPage.filters.cash')}</option>
            <option value="Card">{t('servicesPage.filters.card')}</option>
            <option value="Online">{t('servicesPage.filters.online')}</option>
          </select>
          <div className="flex items-center gap-1.5 border border-gray-200 rounded-lg px-3 py-2 bg-white min-w-56">
            <Search size={12} className="text-gray-400 flex-shrink-0"/>
            <input value={search} onChange={e=>{setSearch(e.target.value);setPage(1)}} placeholder={t('servicesPage.filters.searchPlaceholder')}
              className="text-xs text-gray-600 placeholder-gray-400 focus:outline-none flex-1 bg-transparent min-w-0"/>
            {search && <button onClick={()=>setSearch('')}><X size={11} className="text-gray-300 hover:text-gray-500"/></button>}
          </div>
        </div>
      </div>

      {/* ── Card grid ── */}
      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-5">
        {isLoading
          ? Array.from({length:6}).map((_,i)=><SkeletonCard key={i}/>)
          : rows.length === 0
            ? (
              <div className="col-span-3 flex flex-col items-center justify-center py-20 text-center">
                <div className="w-14 h-14 rounded-full bg-gray-100 flex items-center justify-center mb-3">
                  <FileText size={24} className="text-gray-300"/>
                </div>
                <p className="text-sm font-semibold text-gray-500">{t('servicesPage.empty')}</p>
              </div>
            )
            : rows.map((row:any)=>(
              <ServiceCard key={row.id} row={row} onView={()=>setDetailRow(row)}/>
            ))
        }
      </div>

      {/* ── Pagination ── */}
      {!isLoading && total > 0 && (
        <Paginator page={page} lastPage={lastPage} total={total} perPage={perPage}
          onPage={setPage} onPerPage={n=>{setPerPage(n);setPage(1)}}/>
      )}

      {/* ── Detail Modal ── */}
      <DetailModal
        open={!!detailRow}
        onClose={()=>setDetailRow(null)}
        registration={detailRow}
      />
    </div>
  )
}
